import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useGetCatalogQuery, useGetReleaseKitQuery } from '../../../redux/api/fymAPI';
import { daysUntilRelease } from '../../music24/promote/releases/releaseOrder';
import { formatReleaseWhen } from '../../music24/promote/releases/releaseWhen';

export interface FirstReleaseSummary {
  /** The song title, or null before a song exists or while the catalog loads. */
  title: string | null;
  /** "Out in 29 days", "Out 14 Nov 2026" or "No release date". */
  when: string;
  /** The ready assets of the song's kit (the `planPick` body, design d3). */
  readyAssets: number;
}

/**
 * The facts the card prints about the first-release song: its title, its release day and the
 * ready kit assets. The reads are the ones the Catalog and the release page already cache.
 */
export function useFirstReleaseSummary(
  accountId: number | null | undefined,
  catalogItemUuid: string | null | undefined
): FirstReleaseSummary {
  const intl = useIntl();
  const hasSong =
    accountId !== null && accountId !== undefined && catalogItemUuid !== null && catalogItemUuid !== undefined;
  const skip = !hasSong;
  const catalog = useGetCatalogQuery({ accountId: accountId ?? 0 }, { skip });
  const kit = useGetReleaseKitQuery({ accountId: accountId ?? 0, catalogItemUuid: catalogItemUuid ?? '' }, { skip });

  return useMemo(() => {
    const item = hasSong ? catalog.data?.items.find((row) => row.uuid === catalogItemUuid) : undefined;
    const releaseDate = item?.releaseDate ?? null;
    const readyAssets = (kit.data?.assets ?? []).filter((asset) => asset.status === 'ready').length;
    return {
      title: item?.title ?? null,
      when: formatReleaseWhen(intl, releaseDate, daysUntilRelease(releaseDate, new Date())),
      readyAssets,
    };
  }, [catalog.data, catalogItemUuid, hasSong, intl, kit.data]);
}
